import React, { useState, useMemo } from "react";
import { OrderDirection, Pill, Table, TableColumn, Tooltip } from "@foris/avocado-suite";
import { Icon } from "@foris/avocado-icons";
import {
  ClassroomBooking,
  OrderByDirection,
  ClassroomBookingOrderByFields,
  Session,
} from "../../../../models/ISchema";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { IParams } from "../../../../models/IParams";
import { getColumnCell } from "@common/utils/table.util";
import { classroomsUtils } from "../../utils";
import BookingDatesCell from "./BookingDatesCell";
import DimensionsSearchSkeleton from "@components/dimensions-search-skeleton";
import ClassroomCell from "@modules/booking/components/classroom-cell";
import i18n from "@config/i18n";
import css from "./bookingList.module.scss";

interface IOrderByItem {
  field: ClassroomBookingOrderByFields;
  direction?: OrderByDirection;
}

interface IBookingTableProps {
  bookings: Array<ClassroomBooking>;
  bookingsSelectedIds: Array<string>;
  setBookingSelectedIds: (ids: Array<string>) => void;
  getBookings: (orderBy?: IOrderByItem[]) => void;
  loading: boolean;
}

const formattedDate = (dateTime: string) => {
  const [date] = dateTime.split(" ");
  const [year, month, day] = date.split("-");
  return `${day}.${month}.${year}`;
};
const statusCell = (status: string) => {
  switch (status) {
    case "pending":
      return (
        <Tooltip
          className={css.statusCell}
          label={i18n.t("booking.booking-details.resume.statuses.pending")}
          placement="topLeft"
        >
          <Icon name="clock" size="md" color="neutral-70" />
        </Tooltip>
      );
    case "confirmed":
      return (
        <Tooltip
          className={css.statusCell}
          label={i18n.t("booking.booking-details.resume.statuses.confirmed")}
          placement="topRight"
        >
          <Icon name="check" size="md" color="success-50" />
        </Tooltip>
      );
    case "rejected":
      return (
        <Tooltip
          className={css.statusCell}
          label={i18n.t("booking.booking-details.resume.statuses.rejected")}
          placement="topRight"
        >
          <Icon name="close" size="md" color="error-50" />
        </Tooltip>
      );
  }
};

const BookingTable: React.FC<IBookingTableProps> = (props: IBookingTableProps) => {
  const { setBookingSelectedIds, bookingsSelectedIds, bookings, getBookings, loading } = props;
  const [orderBy, setOrderBy] = useState<IOrderByItem[]>([]);
  const { t } = useTranslation();
  const { origin: originId, scenario: scenarioId, workspace: workspaceId }: IParams = useParams();
  const contextUrl = `${workspaceId}/${scenarioId}/${originId}`;

  const bookingSelectedIdsObj = useMemo(() => {
    return bookingsSelectedIds.reduce(
      (acc, id) => {
        acc[id] = true;
        return acc;
      },
      {} as { [key: string]: boolean },
    );
  }, [bookingsSelectedIds]);

  const hasAllVisibleBookingsSelected = useMemo(
    () =>
      !!bookings?.length &&
      bookings.every(booking => {
        return bookingSelectedIdsObj?.[booking?.id];
      }),
    [bookings, bookingSelectedIdsObj],
  );

  const addBookingSelect = (bookingId: string) => {
    const newBookingSelectedIds = [...bookingsSelectedIds, bookingId];
    setBookingSelectedIds(newBookingSelectedIds);
  };

  const removeBookingSelect = (bookingId: string) => {
    const newBookingSelectedIds = [...bookingsSelectedIds];
    const index = newBookingSelectedIds.indexOf(bookingId);
    newBookingSelectedIds.splice(index, 1);
    setBookingSelectedIds(newBookingSelectedIds);
  };

  const columns: TableColumn[] = [
    {
      header: t("booking.booking-list.table.headers.name"),
      key: ClassroomBookingOrderByFields.Title,
      isSortable: true,
      renderCell: (data: any) => {
        const booking = data as ClassroomBooking;

        return (
          <Link className={css.columnLink} to={`/booking/detail/${contextUrl}/${booking?.id}`}>
            {booking?.title}
          </Link>
        );
      },
      desktopOptions: {
        headCellStyles: { minWidth: "80px" },
      },
    },
    {
      header: t("booking.booking-list.table.headers.date"),
      key: ClassroomBookingOrderByFields.Date,
      isSortable: true,
      renderCell: data => <BookingDatesCell booking={data as ClassroomBooking} />,
      desktopOptions: {
        headCellStyles: { minWidth: "80px", width: "215px" },
      },
    },
    {
      header: t("booking.booking-list.table.headers.classroom"),
      key: ClassroomBookingOrderByFields.Classroom,
      isSortable: true,
      renderCell: (data: any) => {
        if (data?.classroom) {
          return getColumnCell(data?.classroom?.code ?? "-");
        }

        const uniqueClassrooms = classroomsUtils.getUniqueClassroomsFromSessions(data?.sessions);

        return getColumnCell(
          uniqueClassrooms?.length
            ? uniqueClassrooms?.map((classroom, index) => (
                <ClassroomCell
                  key={classroom?.label}
                  classroom={classroom}
                  isLastItem={index === uniqueClassrooms.length - 1}
                />
              ))
            : "-",
          css.classroomCell,
        );
      },
    },
    {
      header: t("booking.booking-list.table.headers.building"),
      key: ClassroomBookingOrderByFields.Building,
      isSortable: true,
      renderCell: (data: any) => {
        if (data?.classroom) {
          return getColumnCell(data?.classroom?.building?.name ?? "-");
        }

        const buildings = (data?.sessions as Session[])
          ?.map(session => session?.classrooms?.map(classroom => classroom?.building?.name))
          .flat();
        const uniqueBuildings = new Set(buildings);
        return getColumnCell(Array.from(uniqueBuildings)?.join(", ") ?? "-");
      },
    },
    {
      header: t("booking.booking-list.table.headers.campus"),
      key: "campus",
      renderCell: (data: any) => {
        if (data?.classroom) {
          return getColumnCell(data?.classroom?.building?.campus?.code ?? "-");
        }

        const campuses = (data?.sessions as Session[])
          ?.map(session => session?.classrooms?.map(classroom => classroom?.building?.campus?.code))
          .flat();
        const uniqueCampuses = new Set(campuses);
        return getColumnCell(Array.from(uniqueCampuses)?.join(", ") ?? "-");
      },
    },
    {
      header: t("booking.booking-list.table.headers.capacity"),
      key: "capacity",
      renderCell: (data: any) =>
        getColumnCell(
          <>
            <b>{data?.capacity ?? "0"}</b>
            de {data?.classroom?.capacity ?? data?.sessions?.[0]?.classrooms?.[0]?.capacity ?? "0"}
          </>,
          css.capacityCell,
        ),
    },
    {
      header: t("booking.booking-list.table.headers.responsible"),
      key: ClassroomBookingOrderByFields.Responsible,
      isSortable: true,
      renderCell: (data: any) =>
        getColumnCell(
          data?.responsible
            ? `${data?.responsible?.name ?? ""} ${data?.responsible?.lastName ?? ""}`
            : "-",
        ),
    },
    {
      header: t("booking.booking-list.table.headers.created-at"),
      key: ClassroomBookingOrderByFields.CreatedAt,
      isSortable: true,
      renderCell: (data: any) => getColumnCell(formattedDate(data?.createdAt)),
      desktopOptions: {
        headCellStyles: { minWidth: "80px", width: "110px" },
      },
    },
    {
      header: t("booking.booking-list.table.headers.status"),
      key: ClassroomBookingOrderByFields.Status,
      isSortable: true,
      renderCell: (data: any) => statusCell(data?.status),
    },
    {
      header: t("booking.booking-list.table.headers.validity"),
      key: "validity",
      isSortable: false,
      renderCell: (data: any) => {
        const booking = data as ClassroomBooking;
        const isActive = booking?.isActive;

        return (
          <div className={css.statusCell}>
            <Pill
              icon={isActive ? "calendar" : undefined}
              iconColor={isActive ? "success-60" : "neutral-90"}
              textColor={isActive ? "success-60" : "neutral-90"}
              color={isActive ? "success-opacity" : "neutral-10"}
            >
              {isActive
                ? t("booking.booking-list.table.rows.active")
                : t("booking.booking-list.table.rows.expired")}
            </Pill>
          </div>
        );
      },
      desktopOptions: {
        headCellStyles: { minWidth: "80px", width: "110px" },
      },
    },
  ];

  if (loading) {
    return <DimensionsSearchSkeleton rows={10} columns={columns?.length} showHeader={false} />;
  }

  if (!bookings) return null;

  return (
    <Table
      className={css.table}
      columns={columns as TableColumn[]}
      data={bookings}
      initialSort={
        orderBy?.length
          ? {
              column: orderBy[0].field,
              direction: orderBy[0].direction?.toLowerCase() as OrderDirection,
            }
          : {
              column: ClassroomBookingOrderByFields.CreatedAt,
              direction: "desc",
            }
      }
      rowOptions={{
        selectorType: "checkbox",
        isHeadSelectorDisabled: !bookings?.length,
        isAllSelected: hasAllVisibleBookingsSelected,
        isSelected(data) {
          const booking = data as ClassroomBooking;
          return !!bookingSelectedIdsObj?.[booking?.id];
        },
        onSelectAll(isSelected) {
          if (isSelected) {
            const newBookingSelectedIds = [...bookings.map(booking => booking.id)]?.filter(
              bookingId => !bookingsSelectedIds?.includes(bookingId),
            );
            setBookingSelectedIds([...bookingsSelectedIds, ...newBookingSelectedIds]);
          } else {
            const bookingsWithoutSelected = bookingsSelectedIds?.filter(
              bookingId => !bookings?.some(booking => booking.id === bookingId),
            );
            setBookingSelectedIds(bookingsWithoutSelected);
          }
        },
        onRowSelect(isSelected, data) {
          const booking = data as ClassroomBooking;

          if (isSelected) {
            addBookingSelect(booking?.id);
          } else {
            removeBookingSelect(booking?.id);
          }
        },
      }}
      onSortChange={newSort => {
        const parsedSort = [
          {
            field: newSort.column as ClassroomBookingOrderByFields,
            direction: newSort.direction?.toUpperCase() as OrderByDirection,
          },
        ];

        setOrderBy(parsedSort);
        getBookings(parsedSort);
      }}
    />
  );
};

export default BookingTable;
